import axiosInstance from '../helpers/axios';

/**
 * Requests for the registrations-cluster features added 1 Sep (transfer,
 * waitlist, eval links, dry-run program charges). Kept out of index.ts so
 * the cluster stays self-contained; same axios client, same conventions.
 */

export type TOpsRegistrationCreateEx = {
  classId: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  waitlist?: boolean;
  programId?: string;
  overrideAmount?: number;
};

export type TOpsTransferResult = { oldId: string; newId: string };

export type TOpsPromoteResult = {
  id: string;
  status: string;
  overCapacity: boolean;
  seats: number | null;
  booked: number | null;
};

export type TOpsEvalLink = { url: string };

export type TOpsAccountProgram = {
  id: string;
  name: string | null;
  status: string | null;
  remaining: number | null;
  endDate: string | null;
  teamPass: boolean;
  /** Subtype matches /anytime/i — charged once per class, not per seat */
  anytimeLicense: boolean;
  /** One line for the booking / class screens, e.g. "Anytime License: charged per class" */
  chargeHint: string;
  ledgerMode: 'dry-run' | 'live';
};

export type TOpsLedgerRow = {
  id: string;
  at: string;
  /** deduction · reversal · adjustment · skipped · *-dryrun */
  type: string;
  /** true when the row moves the balance */
  live: boolean;
  kind: 'registration' | 'event' | 'adjustment';
  amount: number;
  valueConsumed: number;
  note: string | null;
  reason: string | null;
  flags: string[];
  actor: string | null;
  registrationId: string | null;
  learner: string | null;
  classId: string | null;
  className: string | null;
};

export type TOpsProgramLedger = {
  ledgerMode: 'dry-run' | 'live';
  items: TOpsLedgerRow[];
  /** Total of dry-run rehearsals */
  totalAmount: number;
  /** Net of live rows on the balance */
  liveAmount: number;
  /** Net of live rows on the Team Pass tally */
  liveValueConsumed: number;
};

export const createOpsRegistrationEx = async (
  data: TOpsRegistrationCreateEx,
) => {
  const response = await axiosInstance.post<{ id: string; contactId: string }>(
    'ops/registrations',
    data,
  );
  return response.data;
};

export const transferOpsRegistration = async (
  id: string,
  targetClassId: string,
) => {
  const response = await axiosInstance.post<TOpsTransferResult>(
    `ops/registrations/${id}/transfer`,
    { targetClassId },
  );
  return response.data;
};

export const promoteOpsRegistration = async (id: string) => {
  const response = await axiosInstance.post<TOpsPromoteResult>(
    `ops/registrations/${id}/promote`,
    {},
  );
  return response.data;
};

export const getOpsEvalLink = async (registrationId: string) => {
  const response = await axiosInstance.get<TOpsEvalLink>(
    `ops/evals/link/${registrationId}`,
  );
  return response.data;
};

export const getOpsProgramsForAccount = async (accountId: string) => {
  const response = await axiosInstance.get<{ items: TOpsAccountProgram[] }>(
    'ops/programs',
    { params: { accountId, active: '1' } },
  );
  return response.data.items;
};

export const getOpsProgramLedger = async (programId: string) => {
  const response = await axiosInstance.get<TOpsProgramLedger>(
    `ops/programs/${programId}/ledger`,
  );
  return response.data;
};

export const adjustOpsProgram = async (
  programId: string,
  data: { amount: number; note: string },
) => {
  const response = await axiosInstance.post(
    `ops/programs/${programId}/adjust`,
    data,
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────────────
// Program and event fields (18 Sep): the Program Information / Strategy /
// Portal message panels, class delivery details, the prep-call and travel
// queues, and the assignment duration correction. Same endpoints as
// index.ts where they overlap, with the wider response types.
// ─────────────────────────────────────────────────────────────────────────

export const PREP_CALL_STATUSES = [
  'Not Needed',
  'Needed',
  'Requested',
  'Scheduled',
  'Completed',
  'Completed – Prior Prep Call',
] as const;
export const TRAVEL_STATUSES = [
  'Not required',
  'Required – Not yet booked',
  'Required – Initiated',
  'Booked',
  'Invoiced',
] as const;
export const MATERIALS_STATUSES = ['Not needed', 'To ship', 'Shipped'] as const;
export const DELIVERY_METHODS = ['Virtual', 'In-Person', 'Hybrid'] as const;
export const CONTENT_CUSTOMIZATION = ['Standard', 'Custom', 'Both'] as const;
export const PRICING_OPTIONS = [
  'Standard / Current',
  'Custom / Legacy',
  'To Be Clarified',
] as const;
export const MEETING_STATUSES = [
  'To Be Scheduled',
  'Scheduled',
  'Completed',
  'Not Needed',
] as const;
export const LEARNER_REGISTRATION_METHODS = [
  'Online Individual Registration',
  'Roster Submission',
  'Email / Case',
  'Online "Dummy" Account',
  'No Roster / Client',
] as const;

export type TOpsProgramFields = {
  deliveryMethods: string | null;
  contentCustomization: string | null;
  clientFolderUrl: string | null;
  adminPortalAccess: boolean;
  allowPublicEnrollment: boolean;
  descriptionOfSale: string | null;
  pricing: string | null;
  customPricingNotes: string | null;
  publicDiscount: number | null;
  privateDiscount: number | null;
  opportunityType: string | null;
  opportunitySubType: string | null;
  maxMembers: number | null;
  authorizedMembers: number | null;
  contractPocId: string | null;
  secondaryContactId: string | null;
  programLink: string | null;
  renewalProgramId: string | null;
  parentProgramId: string | null;
  disallowOverride: boolean;
  kickOffMeeting: string | null;
  wrapDebrief: string | null;
  clientProgramGoals: string | null;
  successCriteria: string | null;
  programSuccessCriteria: string | null;
  relatedInitiatives: string | null;
  priorityLearningAreas: string | null;
  clientMeetingNotes: string | null;
  portalMessageHeadline: string | null;
  portalMessageBody: string | null;
  learnerRegistrationMethod: string | null;
  prepCallRequirements: string | null;
  prepCallRequirementsNotes: string | null;
  learnerOrientation: string | null;
};

export type TOpsProgramGaps = {
  missing: string[];
  filled: number;
  total: number;
};

export type TOpsProgramContact = {
  id: string;
  name: string | null;
  email: string | null;
  title: string | null;
};

/** A kickoff or wrap slot stored on the program (`_localKickoffAt` / `_localWrapAt`) */
export type TOpsProgramMeeting = {
  start: string;
  end: string;
  timeZone: string | null;
  attendees: string[];
  setBy: string;
  setAt: string;
};

/** The program detail as returned now: the base shape plus the field block */
export type TOpsProgramDetailEx = import('../types').TOpsProgramDetail & {
  accountId: string | null;
  localOrigin: boolean;
  localEditedAt: string | null;
  anytimeLicense: boolean;
  chargeHint: string;
  meetings: {
    kickoff: TOpsProgramMeeting | null;
    wrap: TOpsProgramMeeting | null;
  };
  fields: TOpsProgramFields;
  contractPoc: TOpsProgramContact | null;
  secondaryContact: TOpsProgramContact | null;
  gaps: TOpsProgramGaps;
};

export type TOpsMeetingKind = 'kickoff' | 'wrap';

export type TOpsMeetingCreate = {
  start: string;
  end: string;
  timeZone?: string;
  attendees?: string[];
};

export type TOpsMeetingResult = {
  id: string;
  kind: TOpsMeetingKind;
  status: 'Scheduled';
  meeting: TOpsProgramMeeting;
  /** Signed, no login — what goes in an email */
  icsLink: string;
  /** Staff path, token in the header */
  icsPath: string;
  briefPath: string;
};

export type TOpsProgramListEx = Omit<
  import('../types').TOpsProgramList,
  'items'
> & {
  items: (import('../types').TOpsProgram & { gaps: TOpsProgramGaps })[];
};

/** Partial: only the keys sent are changed. Text '' clears; numbers null clear. */
export type TOpsProgramFieldsUpdate = Partial<{
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  deliveryMethods: string;
  contentCustomization: string;
  clientFolderUrl: string;
  adminPortalAccess: boolean;
  allowPublicEnrollment: boolean;
  descriptionOfSale: string;
  pricing: string;
  customPricingNotes: string;
  publicDiscount: number | null;
  privateDiscount: number | null;
  maxMembers: number | null;
  authorizedMembers: number | null;
  contractPocId: string;
  secondaryContactId: string;
  programLink: string;
  kickOffMeeting: string;
  wrapDebrief: string;
  clientProgramGoals: string;
  successCriteria: string;
  relatedInitiatives: string;
  priorityLearningAreas: string;
  clientMeetingNotes: string;
  portalMessageHeadline: string;
  portalMessageBody: string;
  learnerRegistrationMethod: string;
}>;

export const getOpsProgramEx = async (id: string) => {
  const response = await axiosInstance.get<TOpsProgramDetailEx>(
    `ops/programs/${id}`,
  );
  return response.data;
};

export const getOpsProgramsEx = async (params: {
  q?: string;
  status?: string;
  gaps?: boolean;
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  accountId?: string;
  active?: boolean;
}) => {
  const response = await axiosInstance.get<TOpsProgramListEx>('ops/programs', {
    params: {
      ...params,
      gaps: params.gaps ? 1 : undefined,
      active: params.active ? 1 : undefined,
    },
  });
  return response.data;
};

export const updateOpsProgramFields = async (
  id: string,
  dto: TOpsProgramFieldsUpdate,
) => {
  const response = await axiosInstance.patch<TOpsProgramDetailEx>(
    `ops/programs/${id}`,
    dto,
  );
  return response.data;
};

/** The kickoff brief as HTML (printable, self-contained) — open in a new tab. */
export const getOpsKickoffBriefHtml = async (programId: string) =>
  (
    await axiosInstance.get<string>(`ops/programs/${programId}/kickoff-brief`, {
      responseType: 'text',
      transformResponse: (v) => v,
    })
  ).data;

export const scheduleOpsProgramMeeting = async (
  programId: string,
  kind: TOpsMeetingKind,
  body: TOpsMeetingCreate,
) =>
  (
    await axiosInstance.post<TOpsMeetingResult>(
      `ops/programs/${programId}/${kind}-meeting`,
      body,
    )
  ).data;

/** The meeting's calendar file, as a blob for a download link. */
export const getOpsProgramMeetingIcs = async (
  programId: string,
  kind: TOpsMeetingKind,
) =>
  (
    await axiosInstance.get<Blob>(
      `ops/programs/${programId}/meetings/${kind}.ics`,
      {
        responseType: 'blob',
      },
    )
  ).data;

export const searchOpsProgramContacts = async (
  programId: string,
  q: string,
) => {
  const response = await axiosInstance.get<TOpsProgramContact[]>(
    `ops/programs/${programId}/contacts`,
    { params: { q } },
  );
  return response.data;
};

// ── Classes ────────────────────────────────────────────────────────────

export type TOpsClassDelivery = {
  prepCallStatus: string | null;
  prepCallSource: 'portal' | 'opportunity' | null;
  opportunityPrepCallStatus: string | null;
  customisationHours: number | null;
  customisationNotes: string | null;
  opportunityCustomisationNotes: string | null;
  opportunityContextualNotes: string | null;
  materials: { status: string | null; trackingNote: string | null };
  travelStatus: string | null;
  travelNotes: string | null;
  travelPayee: string | null;
  invoiceCustomerForTravel: boolean;
  confirmOutline: boolean;
  confirmProjectNotes: boolean;
  productMatchesOutline: boolean;
  /** Invoice the customer for this class (T6.07) — set here or read from the deal */
  invoiceDirectly: boolean;
  invoiceDirectlySource: 'portal' | 'opportunity' | null;
  opportunityPaymentType: string | null;
  opportunityInvoiced: boolean;
  invoicedAt: string | null;
  invoiceNote: string | null;
};

export type TOpsCogsBasis =
  | 'rate'
  | 'default-hourly'
  | 'default-flat'
  | 'third-party-cap';

/** Course cost roll-up (T4.23) — on one class's own detail only */
export type TOpsCogs = {
  total: number;
  basis: string;
  lines: {
    assignmentId: string;
    instructor: string | null;
    hours: number;
    rate: number | null;
    amount: number;
    basis: TOpsCogsBasis;
    thirdParty: boolean;
  }[];
};

export type TOpsClassDetailEx = import('../types').TOpsClassDetail & {
  delivery: TOpsClassDelivery;
  cogs: TOpsCogs;
};

export type TOpsClassUpsertEx = import('../types').TOpsClassUpsert & {
  prepCallStatus?: string;
  customisationHours?: number | null;
  customisationNotes?: string;
  materialsStatus?: string;
  materialsNote?: string;
  travelStatus?: string;
  travelNotes?: string;
  travelPayee?: string;
  invoiceCustomerForTravel?: boolean;
  confirmOutline?: boolean;
  confirmProjectNotes?: boolean;
  productMatchesOutline?: boolean;
};

export type TOpsAssignmentEx =
  import('../types').TOpsClassRelated['assignments'][number] & {
    duration: number | null;
    adjustedDuration: number | null;
    /** The "Cost" line for the assignment card — this class only */
    cost: {
      hours: number;
      rate: number | null;
      amount: number;
      basis: TOpsCogsBasis;
    } | null;
  };

export type TOpsClassRelatedEx = Omit<
  import('../types').TOpsClassRelated,
  'assignments'
> & { assignments: TOpsAssignmentEx[]; cogsTotal: number };

export type TOpsQueueItem = {
  id: string;
  name: string;
  classNumber: string | null;
  start: string | null;
  accountName: string | null;
  instructorName: string | null;
  daysOut: number;
  localOrigin: boolean;
};

export type TOpsClassExceptionsEx = import('../types').TOpsClassExceptions & {
  prepCallsDue: {
    days: number;
    count: number;
    items: (TOpsQueueItem & {
      prepCallStatus: string | null;
      prepCallSource: 'portal' | 'opportunity' | null;
    })[];
  };
  travelDue: {
    days: number;
    count: number;
    items: (TOpsQueueItem & {
      classType: string | null;
      travelStatus: string | null;
    })[];
  };
};

export const getOpsClassEx = async (id: string) => {
  const response = await axiosInstance.get<TOpsClassDetailEx>(
    `ops/classes/${id}`,
  );
  return response.data;
};

export const getOpsClassRelatedEx = async (id: string) => {
  const response = await axiosInstance.get<TOpsClassRelatedEx>(
    `ops/classes/${id}/related`,
  );
  return response.data;
};

export const createOpsClassEx = async (dto: TOpsClassUpsertEx) => {
  const response = await axiosInstance.post<TOpsClassDetailEx>(
    'ops/classes',
    dto,
  );
  return response.data;
};

export const updateOpsClassEx = async (id: string, dto: TOpsClassUpsertEx) => {
  const response = await axiosInstance.patch<TOpsClassDetailEx>(
    `ops/classes/${id}`,
    dto,
  );
  return response.data;
};

export const getOpsClassExceptionsEx = async (days = 90) => {
  const response = await axiosInstance.get<TOpsClassExceptionsEx>(
    'ops/classes/exceptions',
    { params: { days } },
  );
  return response.data;
};

export const getOpsClassesEx = async (params: {
  q?: string;
  status?: string;
  classType?: string;
  unassigned?: boolean;
  /** a prep-call status, or 'due' */
  prepCall?: string;
  /** a travel status, or 'due' */
  travel?: string;
  page?: number;
}) => {
  const response = await axiosInstance.get<import('../types').TOpsClassList>(
    'ops/classes',
    { params: { ...params, unassigned: params.unassigned ? 1 : undefined } },
  );
  return response.data;
};

export const setOpsAssignmentDuration = async (
  classId: string,
  assignmentId: string,
  data: { hours: number; note?: string },
) => {
  const response = await axiosInstance.patch<{
    id: string;
    duration: number;
    adjustedDuration: number;
    previous: number | null;
  }>(`ops/classes/${classId}/assignments/${assignmentId}/duration`, data);
  return response.data;
};
